import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import {
  BulkUpsertWorkingconditionsItemDocument,
  BulkUpsertWorkingconditionsItemItemsInput,
  WorkingConditionsVersionDetailsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, FormProvider } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import WorkingConditionVersionView from 'modules/contracts_settings/components/WorkConditions/WorkingConditionVersionView'
import { useWorkConditionsItemsStructure } from 'modules/contracts_settings/lib/useWorkConditionItemsCategories'

import { WorkConditionVersionItemsFormData, validateWorkConditionItems } from './forms'

const WorkingConditionsVersionPage = () => {
  const { company } = useCurrentSession()
  const { identifier } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const navigateToWorkingConditions = (action: 'saved' | 'cancelled') => {
    if (action === 'saved') {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'company_settings.general.employment_data.work_conditions.messages.items_saved_sucessfully'
        ),
      })
    }
    navigate(navigation.settings.contracts_settings.workingConditions.fullPath())
  }

  const [upsertWorkingConditionsItems] = useMutation(BulkUpsertWorkingconditionsItemDocument, {
    extractorFn: (data) => data.workingConditionsMutations.bulkUpsertWorkingconditionsItem,
    refetchQueries: [WorkingConditionsVersionDetailsDocument],
  })

  const { data, loading, error } = useQuery(WorkingConditionsVersionDetailsDocument, {
    variables: {
      companyId: company.id,
      versionId: Number(identifier),
    },
  })

  const items = data?.workingConditions.itemsConnection.nodes || []
  const defaultItemValues = items.reduce<Record<string, string>>((acc, item) => {
    const newItem = { [item.name]: item.value }
    return { ...acc, ...newItem }
  }, {})

  const itemsStructure = useWorkConditionsItemsStructure()
    .map((section) => section.items)
    .flat()
  const dataTypes = itemsStructure.reduce<Record<string, string>>((acc, itemDataType) => {
    const newItem = { [itemDataType.name]: itemDataType.type }
    return { ...acc, ...newItem }
  }, {})

  const toWorkingConditionsItemsInput = (
    form: WorkConditionVersionItemsFormData
  ): BulkUpsertWorkingconditionsItemItemsInput[] => {
    const result = Object.keys(form.items).map((name) => {
      return {
        name: name,
        value: form.items[name] ?? '',
        dataType: form.types[name] ?? '',
      }
    })
    return result
  }

  return (
    <FormProvider
      defaultValues={{
        items: defaultItemValues,
        types: dataTypes,
      }}
      validate={(values) => {
        const errors: FormErrors<WorkConditionVersionItemsFormData> = {}

        const itemErrors = validateWorkConditionItems(values)
        if (itemErrors.items) {
          errors.items = itemErrors.items
        }

        return errors
      }}
      onSubmit={async (values) => {
        const payload = {
          companyId: company.id,
          versionId: data?.workingConditions?.version?.id ?? Number(identifier),
          items: toWorkingConditionsItemsInput(values),
        }

        const result = await upsertWorkingConditionsItems({
          variables: payload,
        })
        const handled = handleResult(result, () => {})
        if (handled.success) {
          navigateToWorkingConditions('saved')
        }

        return handled
      }}
    >
      <WorkingConditionVersionView
        data={data}
        loading={loading}
        error={error}
        onCancel={() => {
          navigateToWorkingConditions('cancelled')
        }}
      />
    </FormProvider>
  )
}

export default WorkingConditionsVersionPage
